import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Star, ShieldCheck, Clock } from "lucide-react";
import { IMG, PHONE, PHONE_HREF } from "./site";
import { EstimateDialog } from "./EstimateDialog";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.25 + i * 0.14 },
  }),
};

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (d) => ({ opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: d } }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-[#FDFBF7] pt-28 lg:pt-32">
      <div className="pointer-events-none absolute inset-0 opacity-[0.35] [background:radial-gradient(#0f172a12_1px,transparent_1px)] [background-size:22px_22px]" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center pb-20">
        <motion.div className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.4, ease: "easeOut", delay: 0.2 }}
            className="relative mb-10 w-44 h-44 sm:w-56 sm:h-56 lg:w-64 lg:h-64"
          >
            <motion.div
              animate={{ opacity: [1, 0.72, 1] }}
              transition={{ duration: 3.5, ease: "easeInOut", repeat: Infinity }}
              className="w-full h-full rounded-full shadow-2xl shadow-[#0F172A]/25 overflow-hidden"
            >
              <img
                src="/images/logo-round.png"
                alt="His and Hers Cleaning Services and Handyman logo"
                data-testid="hero-logo"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </motion.div>

          <motion.div variants={fade} initial="hidden" animate="show" custom={0.1} className="mb-8">
            <span className="inline-flex items-center gap-3 bg-[#0F172A] rounded-full px-6 py-3 shadow-lg shadow-[#0F172A]/20">
              <Star size={17} className="text-[#D97706] fill-[#D97706]" />
              <span data-testid="hero-eyebrow" className="font-jet font-medium text-xs sm:text-sm uppercase tracking-[0.25em] text-[#FDFBF7]">
                Proud Saint Lucie Residents for <span className="text-[#D96B43]">40 Years</span>
              </span>
              <Star size={17} className="text-[#D97706] fill-[#D97706]" />
            </span>
          </motion.div>

          <h1 className="font-display text-[13vw] sm:text-6xl lg:text-7xl xl:text-[5.2rem] leading-[1.02] tracking-tight text-[#0F172A]">
            <span className="block overflow-hidden pb-1">
              <motion.span variants={line} initial="hidden" animate="show" custom={0} className="block">We Complete</motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span variants={line} initial="hidden" animate="show" custom={1} className="block">
                The <em className="italic text-[#D96B43]">Honey-Do</em>
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span variants={line} initial="hidden" animate="show" custom={2} className="block">List.</motion.span>
            </span>
          </h1>

          <motion.p
            variants={fade} initial="hidden" animate="show" custom={0.85}
            data-testid="hero-subheadline"
            className="mt-7 max-w-md text-base sm:text-lg text-[#475569] leading-relaxed"
          >
            One local team for cleaning + handyman across Port St. Lucie &amp; the Treasure Coast.
            Licensed &amp; insured. Same-day estimates.
          </motion.p>

          <motion.div variants={fade} initial="hidden" animate="show" custom={1.05} className="mt-9 flex flex-wrap items-center gap-4">
            <a
              data-testid="hero-call-button"
              href={PHONE_HREF}
              className="group flex items-center gap-3 bg-[#0F172A] text-[#FDFBF7] pl-5 pr-6 py-4 rounded-full font-jet text-sm tracking-wider hover:bg-[#D96B43] transition-colors duration-300"
            >
              <Phone size={16} className="group-hover:rotate-12 transition-transform duration-300" />
              Call {PHONE}
            </a>
            <EstimateDialog
              testId="hero-estimate-button"
              label="Same-Day Estimate"
              className="px-6 py-4 rounded-full border border-[#0F172A]/25 font-jet text-sm tracking-wider text-[#0F172A] hover:border-[#D96B43] hover:text-[#D96B43] transition-colors duration-300"
            />
          </motion.div>

          <motion.div variants={fade} initial="hidden" animate="show" custom={1.25} className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
            <span className="flex items-center gap-2 text-xs font-jet uppercase tracking-[0.15em] text-[#64748B]">
              <ShieldCheck size={15} className="text-[#D96B43]" /> Licensed &amp; Insured · LLC #L26000381246
            </span>
            <span className="flex items-center gap-2 text-xs font-jet uppercase tracking-[0.15em] text-[#64748B]">
              <Clock size={15} className="text-[#D96B43]" /> Same-Day Estimates
            </span>
          </motion.div>

          <motion.div
            variants={fade} initial="hidden" animate="show" custom={1.4}
            data-testid="hero-communication-promise"
            className="mt-6 inline-flex items-center gap-3 bg-[#0F172A] rounded-full pl-4 pr-6 py-3.5 shadow-lg shadow-[#0F172A]/20"
          >
            <span className="relative flex w-3 h-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D96B43] opacity-70" />
              <span className="relative inline-flex rounded-full w-3 h-3 bg-[#D96B43]" />
            </span>
            <span className="font-jet font-medium text-sm sm:text-base tracking-[0.08em] text-[#FDFBF7]">
              You call — we call back. <span className="text-[#D96B43]">Real people, every time.</span>
            </span>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
            className="relative overflow-hidden rounded-t-[10rem] rounded-b-3xl border border-[#D96B43]/20 shadow-2xl shadow-[#0F172A]/10"
          >
            <motion.img
              style={{ y: imgY }}
              src={IMG.crew}
              alt="His and Hers crew — four person team in navy shirts"
              data-testid="hero-crew-image"
              className="w-full h-[420px] lg:h-[540px] object-cover scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/35 via-transparent to-transparent" />
          </motion.div>

          <motion.div
            variants={fade} initial="hidden" animate="show" custom={1.5}
            className="absolute -bottom-6 -left-6 lg:-left-12 bg-white/85 backdrop-blur-xl border border-white/60 shadow-xl rounded-2xl px-6 py-4"
          >
            <span data-testid="hero-years-badge" className="font-display text-4xl text-[#0F172A]">
              40<span className="text-[#D96B43]">+</span>
            </span>
            <span className="block font-jet text-[10px] uppercase tracking-[0.25em] text-[#64748B] mt-1">Years of Local Roots</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
